import { UNIDADES } from "@caint/data";
import { Gauge, SealMedal } from "@caint/design-system";
import type { Eixo, Momento, ResultadoImgq } from "@caint/engine";
import { Fragment, useMemo } from "react";
import {
  META_PRATA,
  META_SUBINDICE,
  type Severidade,
  analiseCritica,
  porEixo,
  porSetor,
} from "../../lib/analiseRelatorio.ts";
import type { AvaliacaoRemota } from "../../lib/api.ts";
import { engine } from "../../lib/engine.ts";
import type { ItemMerged, VisitaEvolucao } from "../../state/useAppState.ts";
import { EvolucaoVisitas } from "../EvolucaoVisitas.tsx";
import { Radar, faixaImgrCor } from "../graficos.tsx";
import { SimuladorMeta } from "../relatorio/SimuladorMeta.tsx";
import { SimuladorSensibilidade } from "./SimuladorSensibilidade.tsx";
import "../../styles/relatorio.css";
import "../../styles/apresentacao.css";

export interface ApresentacaoCicloProps {
  items: ItemMerged[];
  momento: Momento;
  imgq: ResultadoImgq;
  qaci: number;
  ims: number;
  avaliacaoAtual?: AvaliacaoRemota;
  evolucao: VisitaEvolucao[];
}

const NOME_EIXO: Record<Eixo, string> = {
  IMGO: "Gestão Operacional",
  IMQA: "Qualidade Assistencial",
  IMGR: "Gestão de Riscos",
};
const SEV_COR: Record<Severidade, string> = {
  ok: "var(--success)",
  atencao: "var(--warning)",
  critico: "var(--danger)",
};
const SEV_ICO: Record<Severidade, string> = { ok: "✓", atencao: "▲", critico: "⛔" };
const ROMAN: Record<string, string> = { ouro: "III", prata: "II", bronze: "I", dev: "0", pend: "—" };

/** Cor de banda de um score 0–1 (meta 0,46 / Prata 0,60). */
function banda(v: number | null): string {
  if (v == null) return "var(--mat-na)";
  if (v >= META_PRATA) return "var(--success)";
  if (v >= META_SUBINDICE) return "var(--warning)";
  return "var(--danger)";
}

/**
 * Apresentação do Ciclo (doc 04/05): página pronta para projetar/PDF que EXPLICA o
 * IMGQ da unidade — por que este número, quais setores puxam para baixo, as ações
 * mais rápidas para subir, a análise crítica e por que só a maturidade sobe o selo.
 * Reusa o motor (`engine`), as agregações (`analiseRelatorio`) e os gráficos SVG.
 */
export function ApresentacaoCiclo({
  items,
  momento,
  imgq,
  qaci,
  ims,
  avaliacaoAtual,
  evolucao,
}: ApresentacaoCicloProps) {
  const unidadeNome = UNIDADES.find((u) => u.id === avaliacaoAtual?.unidadeId)?.nome ?? "Unidade";
  const selo = engine.selo(imgq.efetivo);

  const eixos = useMemo(() => porEixo(items, momento), [items, momento]);
  const setores = useMemo(() => porSetor(items, momento), [items, momento]);
  const findings = useMemo(
    () =>
      analiseCritica({
        items,
        momento,
        imgq,
        selo: { rotulo: selo.rotulo, nivel: selo.nivel },
        unidadeNome,
      }),
    [items, momento, imgq, selo, unidadeNome],
  );
  const setoresFracos = setores.filter((s) => s.score != null && s.score < META_SUBINDICE);
  const radarSeries = useMemo(
    () => [
      {
        nome: unidadeNome,
        cor: "var(--primary)",
        valores: eixos.map((e) => (e.score == null ? null : e.score * 100)),
      },
    ],
    [eixos, unidadeNome],
  );

  const veredito = imgq.travado
    ? `O selo está TRAVADO em ≤ 59,9% pela regra de exclusão: ${
        imgq.core0 + imgq.core1 > 0 ? `${imgq.core0 + imgq.core1} item(ns) CORE em nível 0/1` : ""
      }${imgq.core0 + imgq.core1 > 0 && setoresFracos.length > 0 ? " e " : ""}${
        setoresFracos.length > 0 ? "eixo(s)/setor(es) abaixo da meta" : ""
      }. Destravar é o primeiro passo — e só a maturidade dos itens destrava.`
    : selo.chave === "dev"
      ? "A unidade está em desenvolvimento (abaixo de 46%). O plano de menor esforço abaixo mostra o caminho mais curto até a meta."
      : `A unidade alcançou o selo ${selo.rotulo}. O foco agora é consolidar os eixos mais frágeis para avançar de faixa.`;

  return (
    <div className="apr-print">
      <div className="rel-cols">
        {/* Capa / resumo */}
        <section className="m-card pad rel-sec rel-span">
          <div className="rel-hero-top">
            <div className="rel-hero-selo">
              <SealMedal chave={selo.chave} roman={ROMAN[selo.chave]} size={64} />
            </div>
            <Gauge value={imgq.efetivo} size={120} />
            <div className="rel-hero-tit">
              <div className="page-eyebrow">Apresentação do Ciclo</div>
              <h2>{unidadeNome}</h2>
              <div className="spec">
                Selo {selo.rotulo || "—"} · IMGQ {engine.pct(imgq.efetivo)}
                {imgq.travado ? " · TRAVADO" : ""}
              </div>
            </div>
          </div>
          <div
            className="rel-veredito"
            style={{ borderLeftColor: imgq.travado ? "var(--danger)" : "var(--warning)" }}
          >
            {veredito}
          </div>
        </section>

        {/* Por que este IMGQ */}
        <section className="m-card pad rel-sec">
          <div className="rel-sec-head">
            <h3>Por que este IMGQ</h3>
            <span className="spec">bruto {engine.pct(imgq.bruto)}</span>
          </div>
          <div style={{ display: "grid", placeItems: "center" }}>
            <Radar eixos={eixos.map((e) => e.eixo)} series={radarSeries} tamanho={240} />
          </div>
          <div className="rel-bars" style={{ marginTop: 8 }}>
            {eixos.map((e) => (
              <div key={e.eixo} className="rel-bar-row">
                <span className="rel-bar-label" title={NOME_EIXO[e.eixo]}>
                  {e.eixo}
                </span>
                <div className="rel-bar-track">
                  <div className="rel-bar-meta" />
                  <div
                    className="rel-bar-fill"
                    style={{ width: `${(e.score ?? 0) * 100}%`, background: banda(e.score) }}
                  />
                </div>
                <span className="rel-bar-val">{e.score == null ? "—" : engine.pct(e.score)}</span>
              </div>
            ))}
          </div>
          <p className="spec rel-nota">
            O IMGQ efetivo ({engine.pct(imgq.efetivo)}) vem da média dos eixos ponderada pelo nº de itens
            {imgq.travado ? (
              <>
                , mas foi <b>rebaixado ao teto de 59,9%</b> pela regra de exclusão. O bruto (
                {engine.pct(imgq.bruto)}) só voltaria a valer depois de destravar.
              </>
            ) : (
              <>. Nenhuma trava ativa: o efetivo é o próprio bruto.</>
            )}
          </p>
        </section>

        {/* Setores com menor avaliação */}
        <section className="m-card pad rel-sec">
          <div className="rel-sec-head">
            <h3>Setores com menor avaliação</h3>
            <span className="spec">{setoresFracos.length} abaixo da meta</span>
          </div>
          <div className="rel-table-wrap" style={{ marginTop: 0 }}>
            <table className="rel-table">
              <thead>
                <tr>
                  <th>Setor</th>
                  <th className="num">Score</th>
                  <th className="num">CORE 0/1</th>
                  <th className="num">NC</th>
                  <th className="num">NA</th>
                </tr>
              </thead>
              <tbody>
                {setores.slice(0, 8).map((s) => {
                  const f = faixaImgrCor(s.score == null ? null : s.score * 100);
                  return (
                    <tr key={s.code}>
                      <td>
                        <span className="rel-dot" style={{ background: f.cor }} />
                        {s.code} · {s.nome}
                      </td>
                      <td className="num" style={{ color: banda(s.score), fontWeight: 700 }}>
                        {s.score == null ? "—" : engine.pct(s.score)}
                      </td>
                      <td className="num" style={s.coreCriticos > 0 ? { color: "var(--danger)" } : undefined}>
                        {s.coreCriticos}
                      </td>
                      <td className="num">{s.nc}</td>
                      <td className="num">{s.na}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="spec rel-nota">
            Ordenados do pior ao melhor. Priorize os setores acima da meta de 46% com itens CORE em 0/1 — são
            os que mais travam e mais rendem ao subir.
          </p>
        </section>

        {/* Ações mais rápidas para subir (plano de menor esforço + peso CORE) */}
        <SimuladorMeta items={items} momento={momento} />

        {/* Análise crítica */}
        <section className="m-card pad rel-sec">
          <div className="rel-sec-head">
            <h3>Análise crítica</h3>
            <span className="spec">{findings.length} dimensões</span>
          </div>
          <div className="rel-findings">
            {findings.map((f) => (
              <div
                key={f.dimensao}
                className="rel-finding"
                style={{ borderLeftColor: SEV_COR[f.severidade] }}
              >
                <div className="rel-finding-head">
                  <span style={{ color: SEV_COR[f.severidade] }}>{SEV_ICO[f.severidade]}</span>
                  <span className="rel-finding-dim spec">{f.dimensao}</span>
                  <b style={{ fontSize: 12.5 }}>{f.titulo}</b>
                </div>
                <p>{f.texto}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Por que só a maturidade sobe o IMGQ (item 3) */}
        <SimuladorSensibilidade items={items} momento={momento} imgq={imgq} />

        {/* Evolução entre visitas */}
        {evolucao.length > 1 ? (
          <section className="m-card pad rel-sec">
            <div className="rel-sec-head">
              <h3>Evolução entre visitas</h3>
              <span className="spec">{evolucao.length} visitas</span>
            </div>
            <EvolucaoVisitas evolucao={evolucao} />
          </section>
        ) : null}
      </div>
      {/* Referência de monitoramento (só contexto; não altera o efetivo em campo). */}
      {(qaci !== 0 || ims !== 0) && (
        <p className="spec" style={{ margin: "10px 4px 0", opacity: 0.8 }}>
          Monitoramento do ciclo — QACI {engine.num2(qaci)} · IMS {engine.num2(ims)} (só afeta a avaliação
          final).
        </p>
      )}
    </div>
  );
}
